import * as React from 'react';
import { cn } from '@/lib/utils';

/* ─── Types ────────────────────────────────────────────────────────────────── */

interface FilterChipItem {
  value: string;
  label: string;
  count?: number;
  disabled?: boolean;
}

interface FilterChipsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  items: FilterChipItem[];
  selected: string[];
  onChange: (selected: string[]) => void;
  multiple?: boolean;
  variant?: 'pills' | 'underline';
  size?: 'sm' | 'default';
}

/* ─── Size class map ──────────────────────────────────────────────────────── */

const sizeClasses = {
  sm: 'text-xs px-2 py-0.5',
  default: 'px-3 py-1.5 text-sm',
} as const;

/* ─── FilterChips ──────────────────────────────────────────────────────────── */

const FilterChips = React.forwardRef<HTMLDivElement, FilterChipsProps>(
  ({ items, selected, onChange, multiple = false, variant = 'pills', size = 'default', className, ...props }, ref) => {
    const handleToggle = (value: string) => {
      if (multiple) {
        const isSelected = selected.includes(value);
        if (isSelected) {
          onChange(selected.filter((v) => v !== value));
        } else {
          onChange([...selected, value]);
        }
      } else {
        // Single-select: toggle or deselect
        if (selected.includes(value)) {
          onChange([]);
        } else {
          onChange([value]);
        }
      }
    };

    const isPills = variant === 'pills';

    return (
      <div
        ref={ref}
        className={cn('flex flex-wrap gap-2', className)}
        role="group"
        {...props}
      >
        {items.map((item) => {
          const isActive = selected.includes(item.value);
          const isDisabled = item.disabled === true;

          return (
            <button
              key={item.value}
              type="button"
              disabled={isDisabled}
              onClick={() => handleToggle(item.value)}
              aria-pressed={isActive}
              className={cn(
                'inline-flex items-center gap-1.5 font-medium transition-colors cursor-pointer',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40',
                sizeClasses[size],
                isDisabled && 'opacity-50 cursor-not-allowed',
                isPills
                  ? cn(
                      'rounded-full border',
                      isActive
                        ? 'bg-raios-accent/15 border-raios-accent/40 text-raios-accent-text'
                        : 'bg-raios-card border-raios-border text-raios-muted hover:text-raios-text-secondary hover:border-raios-elevated'
                    )
                  : cn(
                      'rounded-none border-b-2 bg-transparent',
                      isActive
                        ? 'border-raios-accent text-raios-accent-text'
                        : 'border-transparent text-raios-muted hover:text-raios-text-secondary'
                    )
              )}
            >
              {item.label}
              {item.count != null && (
                <span
                  className={cn(
                    'text-xs tabular-nums',
                    isActive ? 'text-raios-accent-text/70' : 'text-raios-muted/60'
                  )}
                >
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }
);

FilterChips.displayName = 'FilterChips';

export { FilterChips };
export type { FilterChipsProps, FilterChipItem };
